// v3.4.597 — نسخة «مستند Google» من نص العقد المباشر مطابقة لمعاينة A4.
// المشكلة: Google Docs لا يملك خطوط ويندوز العربية (Traditional Arabic…) فيستبدلها بـ Arial الأعرض ~22% → تلتفّ الأسطر
// وتفيض الصفحة وينتقل صف التوقيع للصفحة التالية، بينما المحرر والمعاينة (متصفح المستخدم) سليمان.
// الحل: نقيس النص كما يظهر في المعاينة ثم نُصدر HTML لمستند Google:
//   1) فواصل صفحات صريحة عند حدود صفحات المعاينة نفسها (نفس خوارزمية a4Pagination)
//   2) خط متوفر في Google Docs (Times New Roman للنسخي، Arial للسانس) بحجم مصغَّر بحيث لا يتجاوز عرض النص عرضه في المعاينة
//   3) تباعد أسطر ومسافات فقرات محسوبة من القياس الفعلي (Google يضرب التباعد في ارتفاع السطر الطبيعي للخط ≈ 1.149 من حجمه)
// shrink < 1 يصغّر الخط والمسافات أكثر (يُستخدم آليًا إن زاد عدد صفحات PDF عن المعاينة).
import { paginateBlocks, contentHeightOf, A4 } from "./a4Pagination";

const NAT = 1.149; // ارتفاع السطر الطبيعي لـ Times New Roman / Arial (ascent + descent + lineGap) بالنسبة لحجم الخط
const SAMPLES = ["اتفق الطرفان على أن تكون المدة 16 شهر تبدأ من تاريخ العقد", "يلتزم الطرف الثاني بنقل عداد الكهرباء والمياه 360,000 ريال سعودي", "شركة رئال للتطوير العقاري Unit A3-9"];
const pt = (px) => `${Math.max(1, Math.round(px * 0.75 * 100) / 100)}pt`;

let cv = null;
const widthOf = (family, text) => { cv = cv || document.createElement("canvas").getContext("2d"); cv.font = `100px ${family}`; return cv.measureText(text).width; };
const q = (name) => `"${String(name).replace(/["']/g, "")}"`;
const availCache = {};
function isAvailable(name) {
  if (name in availCache) return availCache[name];
  const a = widthOf(`${q(name)}, monospace`, SAMPLES[0]) !== widthOf("monospace", SAMPLES[0]) || widthOf(`${q(name)}, serif`, SAMPLES[0]) !== widthOf("serif", SAMPLES[0]);
  return (availCache[name] = a);
}
const GENERIC = { serif: "Times New Roman", "sans-serif": "Arial", monospace: "Courier New", "system-ui": "Arial", cursive: "Times New Roman", fantasy: "Times New Roman" };
// الخط المستخدم فعليًا في المتصفح: أول خط متوفر في القائمة، وإلا خط المتصفح الافتراضي (Times New Roman)
export function usedFont(familyList) {
  for (const raw of String(familyList || "").split(",")) {
    const name = raw.trim().replace(/^["']|["']$/g, "");
    if (!name) continue;
    if (GENERIC[name.toLowerCase()]) return GENERIC[name.toLowerCase()];
    if (isAvailable(name)) return name;
  }
  return "Times New Roman";
}
// v3.4.630 — الخطوط العربية النسخية (Traditional / Simplified Arabic، Sakkal Majalla…) تُستبدل في Google بـ «Markazi Text» (متوفر في Google Docs
// وأقرب عرضًا وشكلًا) بحجم قريب من الأصل — كان Times New Roman بحجم مصغَّر جدًا فيبدو النص صغيرًا والصفحة فارغة. يُفعَّل بعد تحميل الخط في المتصفح.
let ARABIC_TARGET = null;
export const setArabicTarget = (name) => { ARABIC_TARGET = name || null; for (const k of Object.keys(kCache)) delete kCache[k]; };
const GOOGLE_FONTS = /^(aref ruqaa|amiri|markazi text|noto naskh arabic|noto kufi arabic|cairo|tajawal|lateef|scheherazade new|reem kufi|el messiri|harmattan)$/i; // v3.4.630
export const targetOf = (used) => GOOGLE_FONTS.test(String(used || "").trim()) ? used : (/arial|tahoma|verdana|segoe|calibri|cairo|sans|helvet/i.test(used) ? "Arial" : /courier|mono/i.test(used) ? "Courier New"
  : ARABIC_TARGET && /arabic|majalla|sakkal|naskh|amiri|traditional|simplified|times|lotus|mohanad|serif/i.test(used) ? ARABIC_TARGET : "Times New Roman");
const kCache = {};
// معامل تصغير الحجم بحيث عرض النص بخط Google ≤ عرضه بخط المعاينة (مع هامش أمان 3%)
export function scaleOf(used, target) {
  if (used === target) return 1; // يشمل خطوط Google المحفوظة كما هي
  const key = used + ">" + target; if (key in kCache) return kCache[key];
  let k = 1;
  for (const s of SAMPLES.slice(0, 2)) { const wu = widthOf(`${q(used)}, serif`, s), wt = widthOf(q(target), s); if (wu > 0 && wt > 0) k = Math.min(k, wu / wt); }
  return (kCache[key] = Math.max(0.6, Math.min(target === ARABIC_TARGET ? 1.15 : 1, k * 0.97)));
}
const hasOwnText = (el) => Array.from(el.childNodes).some((n) => n.nodeType === 3 && n.nodeValue.replace(/[ \t\r\n]+/g, "") !== "");
const BLOCK = /^(block|list-item|table-cell|flow-root)$/;
const FLOW = /^(block|list-item|table|flow-root)$/;

/**
 * @param {string} content نص العقد (كما في المعاينة) @param {object} ps إعدادات صفحة القالب @param {number} shrink 1 = مطابق
 * @returns {{ html: string, pages: number }}
 */
export function buildGoogleHtml(content, ps, shrink = 1) {
  const P = { marginTop: 96, marginBottom: 96, marginLeft: 96, marginRight: 96, fontSize: 14, lineHeight: 1.5, ...(ps || {}) };
  const host = document.createElement("div");
  host.className = "tpl-measure";
  host.style.cssText = `position:absolute;left:0;top:0;visibility:hidden;width:${A4.width - (Number(P.marginLeft) || 96) - (Number(P.marginRight) || 96)}px;font-family:'${String(P.fontFamily || "Traditional Arabic").replace(/'/g, "")}', Arial, sans-serif;font-size:${P.fontSize || 14}px;line-height:${P.lineHeight || 1.5};direction:rtl`;
  host.style.setProperty("--tpl-para", `${Number(P.paraSpacing ?? 8) || 0}px`);
  host.innerHTML = content;
  document.body.appendChild(host);
  try {
    let root = host, wrapStyle = "";
    while (root.children.length === 1 && root.children[0].tagName === "DIV" && root.children[0].children.length > 0 && !root.children[0].classList.contains("page-break")) { wrapStyle += (root.children[0].getAttribute("style") || "") + ";"; root = root.children[0]; }
    const pages = paginateBlocks(root, contentHeightOf(P));
    if (!pages.length) return { html: content, pages: 1 };

    // ── القياس (بلا تعديل): الخط والحجم وارتفاع السطر لكل عنصر ──
    const natCache = {};
    const naturalLh = (family, px) => {
      const key = family + "|" + px; if (key in natCache) return natCache[key];
      const s = document.createElement("div"); s.style.cssText = `position:absolute;visibility:hidden;white-space:nowrap;font-family:${family};font-size:${px}px;line-height:normal`; s.textContent = "اب Ag";
      host.appendChild(s); const h = s.getBoundingClientRect().height; s.remove();
      return (natCache[key] = h || px * 1.2);
    };
    const els = Array.from(root.querySelectorAll("*"));
    const meta = new Map();
    for (const el of els) {
      const cs = getComputedStyle(el);
      const px = parseFloat(cs.fontSize) || 14, used = usedFont(cs.fontFamily), target = targetOf(used);
      meta.set(el, { display: cs.display, target, gpx: px * scaleOf(used, target), lh: cs.lineHeight === "normal" ? naturalLh(cs.fontFamily, px) : (parseFloat(cs.lineHeight) || px * 1.2), text: hasOwnText(el), block: BLOCK.test(cs.display), flow: FLOW.test(cs.display), rect: el.getBoundingClientRect() });
    }
    // ارتفاع السطر لكل فقرة = أعلى قيمة بين دعامة الفقرة ونصوصها، وأكبر حجم خط فعلي فيها
    const blockOf = (el) => { let p = el; while (p && p !== root) { const m = meta.get(p); if (m && m.block) return p; p = p.parentElement; } return null; };
    const agg = new Map();
    for (const el of els) {
      const m = meta.get(el); if (!m.text) continue;
      const b = blockOf(el); if (!b) continue;
      const a = agg.get(b) || { lh: 0, g: 0 }; a.lh = Math.max(a.lh, m.lh); a.g = Math.max(a.g, m.gpx); agg.set(b, a);
    }
    // المسافات الرأسية الفعلية بين الكتل المتتالية (تشمل دمج الهوامش كما في المتصفح)
    const firstOfPage = new Set(pages.map((p) => p.blocks[0])), lastOfPage = new Set(pages.map((p) => p.blocks[p.blocks.length - 1]));
    const gapAfter = new Map();
    for (const el of els) {
      const m = meta.get(el); if (!m.flow || m.rect.height === 0) continue;
      let n = el.nextElementSibling; while (n && (!meta.get(n) || !meta.get(n).flow || meta.get(n).rect.height === 0)) n = n.nextElementSibling;
      gapAfter.set(el, n && !(el.parentElement === root && lastOfPage.has(el)) ? Math.max(0, meta.get(n).rect.top - m.rect.bottom) : 0);
    }

    // ── التطبيق ──
    for (const el of els) {
      const m = meta.get(el);
      if (m.text || m.block || el.style.fontFamily || el.style.fontSize) { el.style.fontFamily = `'${m.target}'`; el.style.fontSize = pt(m.gpx * shrink); }
      if (m.block) {
        const a = agg.get(el); const lh = Math.max(m.lh, a ? a.lh : 0), g = a ? a.g : m.gpx;
        el.style.lineHeight = String(Math.max(0.6, Math.min(3, Math.round((lh / (NAT * g)) * 100) / 100)));
      } else if (m.display === "inline" && el.style.lineHeight) el.style.removeProperty("line-height");
      if (m.flow && /^(P|DIV|H[1-6]|LI|UL|OL|TABLE|BLOCKQUOTE)$/.test(el.tagName)) {
        el.style.marginTop = "0";
        const gp = (gapAfter.get(el) || 0) * shrink;
        el.style.marginBottom = gp > 0.5 ? pt(gp) : "0";
      }
    }
    root.querySelectorAll('br[style*="page-break"]').forEach((b) => b.remove()); // فاصل Word داخل الفقرات — الفواصل الآن صريحة بين الصفحات
    firstOfPage.forEach((el) => { if (el && el.style) el.style.marginTop = "0"; });

    const wrap = (html) => (wrapStyle.replace(/;+/g, "").trim() ? `<div style="${wrapStyle.replace(/"/g, "'")}">${html}</div>` : html);
    const PB = '<hr style="page-break-before:always;display:none;">'; // صيغة فاصل الصفحة التي يصدّرها ويستوردها Google Docs
    return { html: wrap(pages.map((p) => p.blocks.map((el) => el.outerHTML).join("")).join(PB)), pages: pages.length };
  } finally { host.remove(); }
}
